// Gerekçeli dışa aktarım talebi — POST /ops/v1/exports. Dışa aktarımlar sayfası,
// müşteri listesi ve denetim kaydı aynı formu açar; listeler kapsamı
// (`initialKind`) önceden doldurur. Uç dosyayı üretmez, talebi ve filigranı
// kaydeder; form başarı ekranında bunu açıkça söyler.

import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  CopyButton,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Textarea,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";
import { showErrorToast } from "@/lib/error-toast";
import {
  useFrozenRetryableSubmission,
} from "@/lib/idempotency-intent";

type ExportBody =
  paths["/ops/v1/exports"]["post"]["requestBody"]["content"]["application/json"];
export type ExportData = NonNullable<
  paths["/ops/v1/exports"]["post"]["responses"][200]["content"]["application/json"]["data"]
>;

export const EXPORTS_QUERY_KEY = ["operator", "exports"] as const;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FORMATS = ["csv", "json"] as const;
const MIN_REASON_LENGTH = 5;

interface ExportMutationInput {
  readonly body: ExportBody;
  /** Caller-owned immutable intent: retained only for an unchanged retry. */
  readonly idempotencyKey: string;
}

async function requestExport({
  body,
  idempotencyKey,
}: ExportMutationInput): Promise<ExportData> {
  return unwrap(
    operatorGraph.POST("/ops/v1/exports", {
      body,
      params: { header: { "x-idempotency-key": idempotencyKey } },
    }),
  );
}

interface ExportFormState {
  readonly kind: string;
  readonly tenant_id: string;
  readonly columns: string;
  readonly format: (typeof FORMATS)[number];
  readonly reason: string;
}

const EMPTY_FORM: ExportFormState = {
  kind: "",
  tenant_id: "",
  columns: "",
  format: "csv",
  reason: "",
};

/** Virgül-ayrımlı kolon listesi → trim'lenmiş, boşları atılmış dizi. */
function parseColumns(raw: string): string[] {
  return raw
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c !== "");
}

export function RequestExportModal({
  onClose,
  initialKind = "",
  initialTenantId = "",
}: {
  readonly onClose: () => void;
  /** İlk değerler; operatör formda yine de değiştirebilir. */
  readonly initialKind?: string;
  readonly initialTenantId?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ExportFormState>({
    ...EMPTY_FORM,
    kind: initialKind,
    tenant_id: initialTenantId,
  });
  const [result, setResult] = useState<ExportData | null>(null);
  const retryableSubmission = useFrozenRetryableSubmission<ExportBody>();

  const mutation = useMutation({
    mutationFn: requestExport,
    onSuccess: (data): void => {
      retryableSubmission.clear();
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: EXPORTS_QUERY_KEY });
    },
    onError: (error): void => {
      retryableSubmission.settleFailure(error);
      showErrorToast(error, t);
    },
  });

  const set = <K extends keyof ExportFormState>(
    key: K,
    value: ExportFormState[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // tenant_id opsiyonel; verildiyse UUID olmalı (backend ExportScopeSchema).
  const tenantInvalid =
    form.tenant_id.trim() !== "" && !UUID_RE.test(form.tenant_id.trim());
  const formValid =
    form.kind.trim() !== "" &&
    !tenantInvalid &&
    form.reason.trim().length >= MIN_REASON_LENGTH;

  const submit = (): void => {
    if (mutation.isPending) return;
    const pending = retryableSubmission.resume();
    if (pending !== null) {
      mutation.mutate({
        body: pending.payload,
        idempotencyKey: pending.idempotencyKey,
      });
      return;
    }
    if (!formValid) return;
    const columns = parseColumns(form.columns);
    const body: ExportBody = {
      scope: {
        kind: form.kind.trim(),
        ...(form.tenant_id.trim() !== "" && {
          tenant_id: form.tenant_id.trim(),
        }),
      },
      ...(columns.length > 0 && { columns }),
      format: form.format,
      reason: form.reason.trim(),
    };
    const submission = retryableSubmission.reserve(body);
    mutation.mutate({
      body: submission.payload,
      idempotencyKey: submission.idempotencyKey,
    });
  };

  const fieldLabel =
    "mb-1 block font-sans text-[12px] font-medium text-[var(--fg-secondary)]";
  const hint = "mb-0 mt-1 font-sans text-[11px] text-[var(--fg-muted)]";
  const invalidHint =
    "mb-0 mt-1 font-sans text-[11px] text-[var(--state-danger)]";
  const hasPendingSubmission = retryableSubmission.hasPendingSubmission;
  const close = (): void => {
    if (retryableSubmission.locked || mutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      aria-labelledby={titleId}
      closeOnEscape={!retryableSubmission.locked && !mutation.isPending}
      closeOnScrimClick={false}
    >
      <ModalHeader
        id={titleId}
        title={
          result === null
            ? t("exports.request.title")
            : t("exports.request.success_title")
        }
      />
      {result === null ? (
        <>
          <ModalBody>
            <p className="mb-4 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
              {t("exports.request.lede")}
            </p>
            <fieldset
              disabled={hasPendingSubmission}
              className="m-0 min-w-0 border-0 p-0"
            >
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <div>
                  <label htmlFor="export-kind" className={fieldLabel}>
                    {t("exports.request.field.kind")}
                  </label>
                  <Input
                    id="export-kind"
                    size="sm"
                    value={form.kind}
                    onChange={(e): void => set("kind", e.target.value)}
                    placeholder={t("exports.request.placeholder.kind")}
                  />
                  <p className={hint}>{t("exports.request.kind_hint")}</p>
                </div>
                <div>
                  <label htmlFor="export-format" className={fieldLabel}>
                    {t("exports.request.field.format")}
                  </label>
                  <Select
                    id="export-format"
                    value={form.format}
                    onChange={(e): void =>
                      set("format", e.target.value as ExportFormState["format"])
                    }
                  >
                    {FORMATS.map((format) => (
                      <option key={format} value={format}>
                        {t(`exports.format.${format}`)}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="export-tenant-id" className={fieldLabel}>
                    {t("exports.request.field.tenant_id")}
                  </label>
                  <Input
                    id="export-tenant-id"
                    size="sm"
                    value={form.tenant_id}
                    onChange={(e): void => set("tenant_id", e.target.value)}
                    placeholder={t("exports.request.placeholder.tenant_id")}
                    error={tenantInvalid}
                  />
                  <p className={tenantInvalid ? invalidHint : hint}>
                    {tenantInvalid
                      ? t("exports.request.tenant_invalid")
                      : t("exports.request.tenant_hint")}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="export-columns" className={fieldLabel}>
                    {t("exports.request.field.columns")}
                  </label>
                  <Input
                    id="export-columns"
                    size="sm"
                    value={form.columns}
                    onChange={(e): void => set("columns", e.target.value)}
                    placeholder={t("exports.request.placeholder.columns")}
                  />
                  <p className={hint}>{t("exports.request.columns_hint")}</p>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="export-reason" className={fieldLabel}>
                    {t("exports.request.field.reason")}
                  </label>
                  <Textarea
                    id="export-reason"
                    rows={2}
                    value={form.reason}
                    onChange={(e): void => set("reason", e.target.value)}
                    placeholder={t("exports.request.placeholder.reason")}
                  />
                  <p className={hint}>{t("exports.request.reason_hint")}</p>
                </div>
              </div>
            </fieldset>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={close}
              disabled={retryableSubmission.locked || mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={
                mutation.isPending || (!hasPendingSubmission && !formValid)
              }
              loading={mutation.isPending}
            >
              <Download size={14} aria-hidden="true" />
              {t("exports.request.submit")}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            <p className="mb-3 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
              {t("exports.request.success_body")}
            </p>
            <span className={fieldLabel}>
              {t("exports.request.watermark_label")}
            </span>
            <pre className="mb-0 overflow-auto whitespace-pre-wrap break-all rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-3 font-mono text-[11px] leading-relaxed text-[var(--fg-primary)]">
              {result.watermark_fingerprint}
            </pre>
            <p className="mb-0 mt-2 font-serif text-[12.5px] italic text-[var(--fg-muted)]">
              {t("exports.request.success_note")}
            </p>
          </ModalBody>
          <ModalFooter>
            <CopyButton
              value={result.watermark_fingerprint}
              label={t("exports.request.copy")}
              copiedLabel={t("exports.request.copied")}
              idleIcon={<ClipboardCopy size={14} aria-hidden="true" />}
              copiedIcon={<Check size={14} aria-hidden="true" />}
            />
            <Button variant="primary" onClick={onClose}>
              {t("common.done")}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
